import React, { Fragment, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Map, TileLayer } from 'react-leaflet';
import * as L from 'leaflet';
import { GestureHandling } from 'leaflet-gesture-handling';
import {
  filterTemplateStopsForCoords,
  filterTemplateStopTemplatesForCoords,
  getBoundingBox,
  getBoundingBoxCoords,
  getElementsWithSamePositionTemplateStop,
  getNumTemplateStopsOnMap,
  getPolyLinesFromTemplateStopTemplates,
} from '../../../../services/util/mapUtils';
import { MapControls } from '../../MapComponents';
import useMapControls from '../../../../hooks/useMapControls';
import TemplateStopOverlappingMarkerCluster from 'components/Maps/MapComponents/Marker/TemplateStopOverlappingMarkerCluster';
import TemplateStopTemplateStopMarkerCluster from 'components/Maps/MapComponents/Marker/TemplateStopTemplateStopMarkerCluster';
import TemplateStopTemplateMarkerCluster from 'components/Maps/MapComponents/Marker/TemplateStopTemplateMarkerCluster';

const BaseTemplateStopTemplateMap = (props) => {
  const {
    className,
    templates,
    unassignedTemplateStops,
    displayTemplateStops,
    getTemplateStopClusterIcon,
    getUnassignedTemplateStopMarkerAndAddToMcg,
    getTemplateStopMarkerAndAddToMcg,
    getTemplateClusterIcon,
    getOverLappingMarkerClusterIcon,
    displayControls,
    mapState,
    setMapState,
  } = props;

  const map = useRef(null);

  const handleMapMove = () => {
    const mapElement = map.current.leafletElement;
    const center = mapElement.getCenter();
    const zoom = mapElement.getZoom();
    setMapState({ center: [center.lat, center.lng], zoom });
  };

  const templatesWithCoords = filterTemplateStopTemplatesForCoords(templates);
  const unassignedTemplateStopsWithCoords = displayTemplateStops
    ? filterTemplateStopsForCoords(unassignedTemplateStops)
    : [];

  const templateStopsOnMap = getNumTemplateStopsOnMap(
    templatesWithCoords,
    unassignedTemplateStopsWithCoords
  );
  const {
    useCluster,
    setUseCluster,
    useDuplicateSpiderfy,
    setUseDuplicateSpiderfy,
    highLiteDuplicates,
    setHighLiteDuplicates,
  } = useMapControls(templateStopsOnMap, false, false);

  const polyLines = getPolyLinesFromTemplateStopTemplates(templatesWithCoords);

  const overlappingElementGroups = useDuplicateSpiderfy
    ? getElementsWithSamePositionTemplateStop(
      templatesWithCoords,
      unassignedTemplateStopsWithCoords
    )
    : [];
  const keysOfOverlappingElements = overlappingElementGroups.map(
    (g) => g[0].key
  );

  const filteredTemplates = filterTemplateStopTemplatesForCoords(
    templatesWithCoords,
    keysOfOverlappingElements
  );
  const filteredTemplateStops = filterTemplateStopsForCoords(
    unassignedTemplateStopsWithCoords,
    keysOfOverlappingElements
  );

  L.Map.addInitHook('addHandler', 'gestureHandling', GestureHandling);

  const fitMapBoundsToFG = useCallback(() => {
    const coordinates = getBoundingBoxCoords(
      polyLines,
      unassignedTemplateStopsWithCoords
    );
    if (coordinates.length <= 1) return;
    const boundingBox = getBoundingBox(coordinates);
    map.current.leafletElement.fitBounds(boundingBox, { padding: [30, 30] });
    // eslint-disable-next-line
  }, [displayTemplateStops]);

  useEffect(() => {
    fitMapBoundsToFG();
  }, [fitMapBoundsToFG]);

  const renderTemplateStops = () => (
    <TemplateStopTemplateStopMarkerCluster
      getClusterIcon={getTemplateStopClusterIcon}
      getTemplateStopMarkerAndAddToMcg={
        getUnassignedTemplateStopMarkerAndAddToMcg
      }
      templateStops={filteredTemplateStops}
      useCluster={useCluster}
    />
  );

  const renderTemplates = () => (
    <Fragment>
      {filteredTemplates.map((template, index) => (
        <TemplateStopTemplateMarkerCluster
          getClusterIcon={getTemplateClusterIcon}
          getTemplateStopMarkerAndAddToMcg={getTemplateStopMarkerAndAddToMcg}
          key={index}
          polyline={polyLines[index]}
          template={template}
          templateStops={template.stops}
          useCluster={useCluster}
        />
      ))}
    </Fragment>
  );

  return (
    <Fragment>
      <Map
        boundsOptions={{ padding: [10, 10] }}
        center={mapState.center}
        className={className}
        gestureHandling
        onmoveend={handleMapMove}
        onzoomend={handleMapMove}
        ref={map}
        zoom={mapState.zoom}
        zoomSnap={0.25}
      >
        <TileLayer
          attribution='&amp;copy <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Fragment>
          {renderTemplateStops()}
          {renderTemplates()}
        </Fragment>
        {overlappingElementGroups.map((elementGroup, index) => (
          <TemplateStopOverlappingMarkerCluster
            getClusterIcon={getOverLappingMarkerClusterIcon}
            getTemplateStopMarkerAndAddToMcg={getTemplateStopMarkerAndAddToMcg}
            getUnassignedTemplateStopMarkerAndAddToMcg={
              getUnassignedTemplateStopMarkerAndAddToMcg
            }
            highLiteDuplicates={highLiteDuplicates}
            key={index}
            overlappingElements={elementGroup}
          />
        ))}
      </Map>
      {displayControls && (
        <MapControls
          clusteringDisabled={false}
          highLiteDuplicates={highLiteDuplicates}
          setHighLiteDuplicates={setHighLiteDuplicates}
          setUseCluster={setUseCluster}
          setUseDuplicateSpiderfy={setUseDuplicateSpiderfy}
          useCluster={useCluster}
          useDuplicateSpiderfy={useDuplicateSpiderfy}
        />
      )}
    </Fragment>
  );
};

BaseTemplateStopTemplateMap.defaultProps = {
  displayControls: true,
};

BaseTemplateStopTemplateMap.propTypes = {
  className: PropTypes.string,
  displayControls: PropTypes.bool,
  displayTemplateStops: PropTypes.bool.isRequired,
  getOverLappingMarkerClusterIcon: PropTypes.func.isRequired,
  getTemplateStopClusterIcon: PropTypes.func.isRequired,
  getUnassignedTemplateStopMarkerAndAddToMcg: PropTypes.func.isRequired,
  getTemplateClusterIcon: PropTypes.func.isRequired,
  getTemplateStopMarkerAndAddToMcg: PropTypes.func.isRequired,
  unassignedTemplateStops: PropTypes.array.isRequired,
  templates: PropTypes.array,
  mapState: PropTypes.object.isRequired,
  setMapState: PropTypes.func.isRequired,
};

export default BaseTemplateStopTemplateMap;
